import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowRight,
  FaPlay,
  FaClock,
  FaLock,
  FaWandMagicSparkles,
  FaFileSignature,
  FaCertificate,
  FaBookOpen,
  FaGift,
  FaDumbbell,
} from 'react-icons/fa6';
import { fetchJSON, formatDuration } from '../lib/api';
import LessonBody from '../components/LessonBody';

export default function CoursePublic() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleBuy = useCallback(() => {
    setCheckoutLoading(true);
    navigate('/checkout');
  }, [navigate]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchJSON(`/api/courses/${slug}`)
      .then((data) => { if (alive) { setCourse(data.course); setError(null); } })
      .catch((err) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (!course) return;
    document.title = `${course.title} — Learning Outcomes Academy`;
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, [course]);

  if (loading) return <LoadingState />;
  if (error || !course) return <ErrorState message={error || 'Course not found'} />;

  // Separate the free Quick-Start "module" (sort_order 0) from the paid modules
  const quickStart = course.modules.find((m) => m.slug === 'quick-start');
  const paidModules = course.modules.filter((m) => m.slug !== 'quick-start');
  // The course outline shows the six learning modules. Course Complete is a
  // wrap-up + certificate experience after purchase, not a teaching module, so
  // we hide it from the public outline (and call out the certificate below).
  const outlineModules = paidModules.filter((m) => m.slug !== 'course-complete');

  // Count + time the actual teaching content only — exclude module summary
  // pages and the "Resources" link pages so the headline numbers reflect what
  // the parent will spend time on, not navigational/recap pages.
  const teachingLessons = paidModules.flatMap((m) =>
    m.lessons.filter((l) => l.kind !== 'summary' && l.kind !== 'resources')
  );
  const totalSeconds = teachingLessons.reduce(
    (s, l) => s + (l.duration_seconds || 0),
    0
  );
  const quickStartHref = quickStart && quickStart.lessons[0]
    ? `/courses/${course.slug}/${quickStart.slug}/${quickStart.lessons[0].slug}`
    : null;

  const outcomes = Array.isArray(course.outcomes) ? course.outcomes : [];
  const differentiators = Array.isArray(course.differentiators) ? course.differentiators : [];
  const descriptionParas = (course.description || '').split(/\n\s*\n/).filter(Boolean);

  return (
    <div className="w-full bg-bone">

      {/* HERO ─────────────────────────────────────────── */}
      <section className="bg-bone">
        <div className="container-page pt-14 lg:pt-20 pb-12 lg:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow">
              <span className="inline-block h-px w-8 bg-brand-deep mr-2 align-middle" />
              A Learning Outcomes Academy course
            </div>
            {(() => {
              const [main, ...rest] = course.title.split(/:\s*/);
              const accent = rest.join(': ');
              return (
                <>
                  <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tighter2">
                    {main}
                  </h1>
                  {accent && (
                    <p className="mt-5 font-display italic text-xl sm:text-2xl lg:text-[28px] text-brand-moss leading-snug max-w-2xl">
                      {accent}
                    </p>
                  )}
                </>
              );
            })()}
            {course.subtitle && (
              <p className="mt-5 text-lg leading-relaxed text-slate2 max-w-2xl">{course.subtitle}</p>
            )}

            <div className="mt-8 flex flex-col items-start gap-4">
              <div className="flex flex-col items-start gap-1.5">
                <button
                  type="button"
                  onClick={handleBuy}
                  disabled={checkoutLoading}
                  className="btn-primary disabled:opacity-60 disabled:cursor-wait"
                >
                  {checkoutLoading ? (
                    'Loading checkout…'
                  ) : (
                    <>
                      <span>Buy course</span>
                      <span className="mx-0.5 opacity-50">—</span>
                      <span className="line-through opacity-50 text-sm font-normal">CA$34.99</span>
                      <span>CA$14.99</span>
                    </>
                  )}
                  {!checkoutLoading && <FaArrowRight className="text-xs" />}
                </button>
                <p className="text-xs text-slate2/80 pl-1">
                  one-time purchase · lifetime access ·{' '}
                  <Link
                    to={`/courses/${course.slug}/gift`}
                    className="inline-flex items-center gap-1 font-semibold text-brand-deep underline underline-offset-4 hover:text-ink"
                  >
                    <FaGift className="text-[10px]" /> Gift this course
                  </Link>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {quickStartHref && (
                  <Link to={quickStartHref} className="btn-link">
                    <FaWandMagicSparkles className="text-xs" />
                    Read the free Quick-Start guide
                  </Link>
                )}
                {quickStartHref && (
                  <Link to={`${quickStartHref}#welcome-video`} className="btn-link">
                    <FaPlay className="text-xs" />
                    Watch a 2-min welcome
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Quick-facts card */}
          <div className="lg:col-span-5">
            <div className="card-soft p-6 lg:p-7 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand-sage/30" />
              <div className="absolute -bottom-12 -left-8 w-32 h-32 rounded-full bg-brand-aubergine/15" />
              <div className="relative">
                <div className="text-[11px] tracking-[0.18em] uppercase text-brand-deep">
                  What's included
                </div>
                <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <Fact
                    label={`${outlineModules.length} modules`}
                    sub="50 lessons"
                    icon={<FaBookOpen />}
                  />
                  <Fact
                    label={
                      totalSeconds >= 3600
                        ? `Over ${Math.floor(totalSeconds / 3600)} hours`
                        : formatDuration(totalSeconds) || '~2 hours'
                    }
                    sub="at your own pace"
                    icon={<FaClock />}
                  />
                  <Fact
                    label="Over 30 activities"
                    sub={'\u201CTry This Now\u201D exercises'}
                    icon={<FaDumbbell />}
                  />
                  <Fact
                    label="Family agreement"
                    sub="ready-to-edit template"
                    icon={<FaFileSignature />}
                  />
                  <Fact
                    label="Certificate"
                    sub="awarded on completion"
                    icon={<FaCertificate />}
                  />
                  <Fact
                    label="Watch + listen"
                    sub="8 short videos + AI podcast"
                    icon={<FaPlay />}
                  />
                </ul>
                <div className="mt-6 pt-6 border-t border-ink/10">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-ink/45 line-through">CA$34.99</span>
                    <span className="font-display text-xl tracking-tighter2 text-ink">CA$14.99</span>
                    <span className="text-xs bg-brand-aubergine/10 text-brand-aubergine font-medium px-2 py-0.5 rounded-full">Introductory Price</span>
                  </div>
                  <p className="text-xs text-slate2 mt-1">no subscription · lifetime access</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DESCRIPTION ─────────────────────────────────── */}
      {descriptionParas.length > 0 && (
        <section className="bg-cream">
          <div className="container-page py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="eyebrow eyebrow-bar">About this course</div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
                Built for parents,{' '}
                <span className="italic text-brand-aubergine">not engineers.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 space-y-5">
              {descriptionParas.map((para, i) => (
                <p key={i} className="text-lg leading-relaxed text-ink/90">
                  {para}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHAT YOU'LL LEARN ────────────────────────────── */}
      {outcomes.length > 0 && (
        <section className="bg-bone">
          <div className="container-page py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow eyebrow-bar">What you'll learn</div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
                By the end of the course,{' '}
                <span className="italic text-brand-deep">you'll be able to:</span>
              </h2>
              <p className="mt-5 text-slate2 leading-relaxed">
                Seven concrete outcomes — not abstract ideas. Each one maps to specific lessons,
                activities, and conversations you can have at home.
              </p>
            </div>
            <ul className="lg:col-span-6 lg:col-start-7 space-y-4">
              {outcomes.map((line, i) => (
                <li key={i} className="flex items-start gap-3 pb-4 border-b border-ink/10 last:border-0">
                  <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-deep text-bone text-[10px] font-display">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-ink/90 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* MODULES OUTLINE ─────────────────────────────── */}
      <section id="modules" className="bg-cream scroll-mt-24">
        <div className="container-page py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-10 lg:mb-14">
            <div className="lg:col-span-7">
              <div className="eyebrow eyebrow-bar">Course outline</div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish">
                Six modules, paced for{' '}
                <span className="italic text-brand-deep">busy parents.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate2 leading-relaxed">
                Each module is short enough to finish in a single sitting, with a video, written guide,
                and a companion podcast you can listen to in the car or while folding laundry.
              </p>
            </div>
          </div>

          <ol className="divide-y divide-ink/10 border-t border-b border-ink/10">
            {outlineModules.map((m, idx) => {
              // Count + time exclude summary/resources rows so the displayed
              // figures match the "What's Included" totals and reflect real
              // teaching content. The navigation TOC in the lesson page is
              // unaffected and still lists every lesson for clickable access.
              const teachingForCount = m.lessons.filter(
                (l) =>
                  l.slug !== 'coming-soon' &&
                  l.kind !== 'intro' &&
                  l.kind !== 'summary' &&
                  l.kind !== 'resources'
              );
              const moduleSeconds = m.lessons
                .filter((l) => l.slug !== 'coming-soon' && l.kind !== 'summary')
                .reduce((s, l) => s + (l.duration_seconds || 0), 0);
              const realLessonCount = teachingForCount.length;
              const isPlaceholder = realLessonCount === 0;
              return (
                <li key={m.id} className="py-7 lg:py-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start group">
                  <div className="lg:col-span-1 font-display text-3xl text-brand-deep/40 tracking-tighter2">
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                  <div className="lg:col-span-7">
                    <h3 className="font-display text-2xl leading-snug text-ink">
                      {/* Strip the "Module N: " prefix in display since we already show 0N */}
                      {m.title.replace(/^Module\s+\d+:\s*/i, '')}
                    </h3>
                    {m.summary && (
                      <p className="mt-2 text-slate2 leading-relaxed">{m.summary}</p>
                    )}
                  </div>
                  <div className="lg:col-span-4 lg:text-right">
                    <div className="inline-flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate2">
                      {isPlaceholder ? (
                        <span className="px-2 py-0.5 rounded-full bg-brand-aubergine/10 text-brand-aubergine font-medium tracking-wide uppercase text-[10px]">
                          Coming soon
                        </span>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1.5">
                            <FaPlay className="text-[10px]" />
                            {realLessonCount} lesson{realLessonCount === 1 ? '' : 's'}
                          </span>
                          {(moduleSeconds > 0 || m.estimated_minutes) && (
                            <span className="inline-flex items-center gap-1.5">
                              <FaClock className="text-[10px]" />
                              {moduleSeconds > 0
                                ? formatDuration(moduleSeconds)
                                : `${m.estimated_minutes} min`}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-6 gap-y-2 text-xs text-slate2">
            <span className="inline-flex items-center gap-2">
              <FaLock className="text-[10px]" />
              Lessons unlock after purchase. The Quick-Start guide above is free.
            </span>
          </div>

          {/* CERTIFICATE INFO — editorial close to the modules section.
              Static information, not a CTA: active learners reach the actual
              final assessment from the lesson sidebar, not from this page.
              Mirrors the eyebrow/headline/paragraph grid of the section
              header above so the section opens and closes in the same voice. */}
          <CertificateInfoBlock />
        </div>
      </section>

      {/* WHAT MAKES THIS DIFFERENT ────────────────────── */}
      {differentiators.length > 0 && (
        <section className="bg-bone">
          <div className="container-page py-16 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
              <div className="lg:col-span-5">
                <div className="eyebrow eyebrow-bar">What makes this different</div>
                <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish">
                  Practical, balanced, and{' '}
                  <span className="italic text-brand-aubergine">made for real households.</span>
                </h2>
              </div>
              <div className="lg:col-span-6 lg:col-start-7 self-end">
                <p className="text-slate2 leading-relaxed">
                  We've taken a deliberate stance: not a panic piece, not a tech-bro pep talk —
                  just clear guidance grounded in how families actually live with technology.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {differentiators.map((d, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-ink/10 bg-cream/60 p-6 lg:p-7"
                >
                  <div className="font-display text-xl text-ink leading-snug">
                    {d.title}
                  </div>
                  <p className="mt-2 text-slate2 leading-relaxed text-sm">{d.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FACILITATOR ──────────────────────────────────── */}
      {course.facilitator_md && (
        <section className="bg-ink text-bone">
          <div className="container-page py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-4">
              <div className="eyebrow !text-brand-sage">
                <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
                Meet your facilitator
              </div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight">
                Over two decades of teaching,{' '}
                <span className="italic text-brand-sage">applied to everyday family decisions about AI.</span>
              </h2>
            </div>
            <div className="lg:col-span-8">
              <div className="rounded-2xl bg-bone/5 border border-bone/10 p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-7">
                  <img
                    src="/courses/ai-for-parents/dr-q-circuit.jpg"
                    alt="Qusay H. Mahmoud (Dr. Q)"
                    className="w-32 h-32 sm:w-36 sm:h-36 rounded-2xl object-cover flex-shrink-0 shadow-[0_12px_24px_-8px_rgba(0,0,0,0.55)]"
                  />
                  <div className="prose-lesson prose-lesson--invert flex-1 min-w-0">
                    <LessonBody md={course.facilitator_md} invert />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CLOSING CTA ──────────────────────────────────── */}
      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24">
          <div className="rounded-3xl bg-brand-deep text-bone p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.18em] uppercase text-brand-sage">
                Ready when you are
              </div>
              <h2 className="mt-3 font-display text-3xl lg:text-4xl leading-tight">
                Get the course for{' '}
                <span className="italic text-brand-sage">CA$14.99</span>{' '}
                <span className="line-through text-bone/40 text-2xl lg:text-3xl">CA$34.99</span>{' '}
                — and bring the AI conversation home.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:text-right space-y-3">
              <button
                type="button"
                onClick={handleBuy}
                disabled={checkoutLoading}
                className="inline-flex items-center justify-center gap-2 bg-bone text-ink font-medium px-6 py-3 rounded-full hover:bg-brand-sage transition-colors disabled:opacity-60 disabled:cursor-wait"
              >
                {checkoutLoading ? (
                  'Loading checkout…'
                ) : (
                  <>
                    <span>Buy now</span>
                    <span className="mx-0.5 opacity-50">·</span>
                    <span className="line-through opacity-50 text-sm font-normal">CA$34.99</span>
                    <span>CA$14.99</span>
                  </>
                )}
                {!checkoutLoading && <FaArrowRight className="text-xs" />}
              </button>
              <div className="flex flex-col items-end gap-2 text-xs text-bone/70">
                <span>one-time purchase · lifetime access</span>
                <Link
                  to={`/courses/${course.slug}/gift`}
                  className="inline-flex items-center gap-1 font-semibold text-bone hover:text-bone/80 underline underline-offset-4"
                >
                  <FaGift className="text-[10px]" /> Gift this course
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


function Fact({ label, sub, icon }) {
  return (
    <li>
      <div className="font-display text-base text-ink leading-tight inline-flex items-center gap-2">
        {icon && <span className="text-brand-deep text-sm">{icon}</span>}
        {label}
      </div>
      <div className="text-xs text-slate2 mt-0.5">{sub}</div>
    </li>
  );
}

// Editorial certificate-info block sitting under the modules outline.
// Mirrors the section's own header layout (eyebrow + headline on the left,
// supporting paragraph on the right) so the modules section closes in the
// same visual voice it opened in. No box, no button — typography only.
function CertificateInfoBlock() {
  return (
    <div className="mt-12 lg:mt-16 pt-10 lg:pt-12 border-t border-ink/10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="eyebrow eyebrow-bar">Certificate of Completion</div>
          <h3 className="mt-5 font-display text-2xl lg:text-3xl leading-tight tracking-tightish text-ink">
            Earn your Certificate{' '}
            <span className="italic text-brand-deep">of Completion.</span>
          </h3>
        </div>
        <div className="lg:col-span-5">
          <p className="text-slate2 leading-relaxed">
            Answer the Check Your Understanding question at the end of each lesson as you go,
            then pass the 18-question final assessment with a score of 70% or higher.
          </p>
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="container-page py-24">
      <div className="animate-pulse space-y-6">
        <div className="h-3 w-40 bg-ink/10 rounded" />
        <div className="h-12 w-3/4 bg-ink/10 rounded" />
        <div className="h-4 w-2/3 bg-ink/10 rounded" />
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="container-page py-24 text-center">
      <div className="eyebrow eyebrow-bar justify-center inline-flex">Course unavailable</div>
      <h1 className="mt-5 font-display text-3xl text-ink">{message}</h1>
      <Link to="/courses" className="btn-link mt-6 inline-flex">
        Back to courses <FaArrowRight className="text-xs" />
      </Link>
    </div>
  );
}
